import React, { Fragment, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { history } from '../../';
import axios from 'axios';
import clsx from 'clsx';

import { isWidthDown, isWidthUp } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';

import pushToGTM, { pageLoadGA4Tag } from '../../utils/analytics/gtm';
import { tabRoutes } from '../../routes';
import { NUMBER_OF_SKELETON_TABLE_ROWS } from '../../styles/constants';
import { AXIOS_TIMEOUT } from '../../utils/constants';
import { OPT_SELECT_TABLE_COLUMNS } from '../../components/SelectTable/constants';
import tabbarSlice from '../../components/Tabbar/tabbarSlice';
import designListSlice from '../../redux/slices/designListSlice';
import selectTableSlice from '../../components/SelectTable/selectTableSlice';
import { useStyles } from './styles';
import SidepanelLayout from '../SidepanelLayout';
import ErrorPage from '../ErrorPage';
import Filterbar from '../../components/Filterbar';
import InputParameters from '../../components/InputParameters';
import CalculationResults from '../../components/CalculationResults';
import SelectTable from '../../components/SelectTable';
import TableSkeleton from '../../components/Table/TableSkeleton';
import OptionalColumnsSelect from '../../components/OptionalColumnsSelect';
import Search from '../../components/Search';
import { localView } from '../../utils/analytics/local';

const SelectPage = ({ screenWidth }) => {
	const classes = useStyles();
	const [hasError, setHasError] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const disabledTabs = useSelector(state => state.tabbar.disabledTabs);
	const fetchDesignListData = useSelector(state => state.designList.fetchDesignListData);
	const inputParametersDL = useSelector(state => state.designList.inputParameters);
	const progressActive = useSelector(state => state.tabbar.progressActive);
	const dispatch = useDispatch();

	useEffect(() => {
		localView('Select');
		pushToGTM(pageLoadGA4Tag('SELECT'));
		let timer;

		if (disabledTabs.Select) {
			history.push(tabRoutes.find(route => route.path === '/inputs').path);
		} else if (fetchDesignListData) {
			timer = setTimeout(() => {
				axios
					.post('api/calc/designList', inputParametersDL, {
						timeout: AXIOS_TIMEOUT
					})
					.then(response => {
						try {
							if (response.data) {
								if (response.data.allCalculatedDesigns.length > 0) {
									dispatch(designListSlice.actions.setItems(response.data.allCalculatedDesigns));
									dispatch(
										designListSlice.actions.setFiltersConstants({
											items: response.data.allCalculatedDesigns,
											rangeFilters: response.data.rangeFilters,
											icPackages: response.data.setOfIcPackages
										})
									);
									dispatch(
										selectTableSlice.actions.setOptionalColumns(
											extractOptionalColumns(response.data.allCalculatedDesigns)
										)
									);
								}
							}
						} catch (e) {
							console.error('Error fetching data for Select Page:', e);
							setHasError(true);
						}
					})
					.catch(() => {
						dispatch(designListSlice.actions.setItems(null));
						setHasError(true);
					})
					.finally(() => {
						dispatch(tabbarSlice.actions.setProgressActive(false));
						dispatch(designListSlice.actions.setFetchDesignListData(false));
					});
			}, 500);
		} else {
			dispatch(tabbarSlice.actions.setProgressActive(false));
		}

		return () => {
			clearTimeout(timer);
		};
	}, [dispatch, disabledTabs]);

	const extractOptionalColumns = designList => {
		const optColumnKeys = OPT_SELECT_TABLE_COLUMNS.filter(
			optCol => designList.filter(design => design[optCol.key] !== null && design[optCol.key] !== undefined).length > 0
		);

		return new Array(OPT_SELECT_TABLE_COLUMNS.find(col => col.key === 'all')).concat(optColumnKeys.filter(k => k));
	};

	if (!progressActive && hasError) {
		return <ErrorPage destinationPage={'/inputs'} />;
	}

	return (
		<SidepanelLayout
			sidepanel={
				<Grid
					container
					direction={isWidthUp('md', screenWidth) ? 'column' : 'column-reverse'}
					spacing={isWidthDown('sm', screenWidth) ? 2 : 3}>
					<Grid item className={classes.searchContainer}>
						<Search searchValue={searchValue} setSearchValue={setSearchValue} />
					</Grid>

					<Grid
						item
						container
						direction={screenWidth === 'sm' ? 'row' : 'column'}
						spacing={isWidthDown('sm', screenWidth) ? 2 : 3}>
						<Grid item container xs={screenWidth === 'sm' ? 6 : false}>
							<InputParameters
								withChangeButton
								inputParameters={inputParametersDL}
								isVinNomSet={inputParametersDL.vinNom !== ''}
							/>
						</Grid>

						<Grid item container xs={screenWidth === 'sm' ? 6 : false}>
							<CalculationResults />
						</Grid>
					</Grid>
				</Grid>
			}
			content={
				<Fragment>
					<Grid container justify="space-between" alignItems="flex-start" className={classes.filterbarContainer}>
						<Grid item className={classes.filterbarItem}>
							<Filterbar searchValue={searchValue} setSearchValue={setSearchValue} screenWidth={screenWidth} />
						</Grid>

						<Grid item className={clsx(classes.filterbarItem, classes.optionalColumnsSelect)}>
							<OptionalColumnsSelect />
						</Grid>
					</Grid>

					{progressActive ? <TableSkeleton numberOfRows={NUMBER_OF_SKELETON_TABLE_ROWS} /> : <SelectTable />}
				</Fragment>
			}
		/>
	);
};

SelectPage.propTypes = {
	screenWidth: PropTypes.number.isRequired
};

export default SelectPage;
